import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from '@/components_deprecated/Modal'
import { Form } from '@/components_deprecated/Form'
import { TextArea } from '@/components_deprecated'
import { SalaryCodeSelect } from '@/components_deprecated/Select/SalaryCodeSelect'
import styled, { useTheme } from 'styled-components'
import { isMobile } from 'react-device-detect'
import { DatePicker } from '@/components'
import { set } from 'date-fns'

interface FieldProps {
  alignLeft?: boolean
  alignRight?: boolean
  area?: string
}
export const Field = styled.div<FieldProps>`
  grid-area: ${props => props.area};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${props => props.alignLeft && 'align-items: flex-start;'}
  ${props => props.alignRight && 'align-items: flex-end;'}

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    color: ${props => props.theme.colors.gray5};
  }
`

const StyledForm = styled(Form)`
  gap: 0.5rem;
`

const DateTimeField = styled(Field)`
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 0.5rem;
  width: 100%;

  label {
    grid-row: 1;
    grid-column: 1 / 3;
    margin: 0 0 0 0;
  }
`

export interface ConfigureFixedAddonPromptForm {
  validFrom: Date | null
  validTo: Date | null
  salaryCode: string | null
  salaryCodeIfOvertime: string | null
  comment: string
}

interface ConfigureFixedAddonPromptProps {
  initialData?: ConfigureFixedAddonPromptForm
  userId?: string
  onSubmit: (data: ConfigureFixedAddonPromptForm | null) => void
}

export const ConfigureFixedAddonPrompt: React.FC<
  ConfigureFixedAddonPromptProps
> = ({
  initialData,

  onSubmit,
}) => {
  const translations = useTranslate({
    label: {
      datetimeStart: 'common.start',
      datetimeEnd: 'common.end',
      salaryCode: 'common.salary-code',
      salaryCodeIfOvertime: 'common.salary-code-if-overtime',
      overrideOtherAddons: 'salary.override-other-addons',
      comment: 'common.comment',
    },
    create: 'common.create-alt',
    update: 'common.update',

    error: {
      startBeforeEnd: 'timesheets.error.start-before-end',
      required: 'common.required',
      overlap: 'salary.error.addon-overlap',
    },
  })
  const theme = useTheme()

  const { formValues, formChangeHandler, formValid, formErrors, updateForm } =
    useForm<ConfigureFixedAddonPromptForm>({
      values: initialData ?? {
        validFrom: new Date(),
        validTo: new Date(),
        salaryCode: '',
        salaryCodeIfOvertime: null,
        comment: '',
      },
      config: {
        initAsInvalid: true,
      },
      validators: {
        validFrom: (value, other) => {
          if (!value || !other.validTo) {
            return null
          }
          return value < other.validTo
            ? null
            : translations.error.startBeforeEnd
        },
        validTo: (value, other) => {
          if (!value || !other.validFrom) {
            return null
          }
          return value > other.validFrom
            ? null
            : translations.error.startBeforeEnd
        },
        salaryCode: value => {
          return value ? null : translations.error.required
        },
      },
    })

  function updateDate(key: 'validFrom' | 'validTo') {
    return (value: Date | null) => {
      const existingValue = formValues[key] ?? new Date()
      if (!value) {
        return
      }
      const newValue = set(existingValue, {
        year: value.getFullYear(),
        month: value.getMonth(),
        date: value.getDate(),
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      })

      updateForm({
        [key]: newValue,
      })
    }
  }

  return (
    <RegularModal
      title={translations.create}
      submitText={translations.update}
      submitDisabled={!formValid}
      onCancel={() => onSubmit(null)}
      onSubmit={() => onSubmit(formValues)}
      width={isMobile ? '100%' : '500px'}
    >
      <StyledForm onSubmit={() => onSubmit(formValues)}>
        <DateTimeField>
          <label>{translations.label.datetimeStart}</label>

          <DatePicker
            value={formValues.validFrom}
            onChange={value => updateDate('validFrom')(value)}
            mode="single"
            className="w-full"
          />
          {formErrors.validFrom && (
            <span
              className="text-red absolute top-0 right-0"
              style={{ color: theme.colors.red }}
            >
              {formErrors.validFrom}
            </span>
          )}
        </DateTimeField>
        <DateTimeField>
          <label>{translations.label.datetimeEnd}</label>
          <DatePicker
            value={formValues.validTo}
            onChange={value => updateDate('validTo')(value)}
            mode="single"
            className="w-full"
          />
          {formErrors.validTo && (
            <span
              className="red absolute top-0 right-0"
              style={{ color: theme.colors.red }}
            >
              {formErrors.validTo}
            </span>
          )}
        </DateTimeField>
        <Field>
          <label>{translations.label.salaryCode}</label>
          <SalaryCodeSelect
            value={formValues.salaryCode}
            onChange={value =>
              value ? formChangeHandler('salaryCode')(value) : null
            }
            grayed
          />
        </Field>
        <Field>
          <label>{translations.label.salaryCodeIfOvertime}</label>
          <SalaryCodeSelect
            value={formValues.salaryCodeIfOvertime}
            onChange={value =>
              value ? formChangeHandler('salaryCodeIfOvertime')(value) : null
            }
            grayed
          />
        </Field>
        <Field>
          <label>{translations.label.comment}</label>
          <TextArea
            value={formValues.comment}
            onChange={formChangeHandler('comment')}
            rows={3}
          />
        </Field>
      </StyledForm>
    </RegularModal>
  )
}
